import classnames from 'classnames';
import flatten from 'lodash/flatten';
import isEqual from 'lodash/isEqual';
import isFunction from 'lodash/isFunction';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import reduce from 'lodash/reduce';
import { Dropdown } from 'primereact/dropdown';
import PropTypes from 'prop-types';
import React from 'react';
import { SegmentEventTypes } from '../../data/tracking';
import Analytics from '../../utils/Analytics';
import LabelWrapper from '../LabelWrapper.component';

export class WrappedGroupedDropdown extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			groupedOptions: null,
		};
	}

	componentDidMount() {
		this.setState({
			groupedOptions: this.buildGroupedOptions(),
		});
	}

	componentDidUpdate(prevProps) {
		const { options } = this.props;

		if (!isEqual(options, prevProps.options)) {
			this.updateState();
		}
	}

	/**
	 * Return an array of grouped options based on `groupOptionValue` prop value.
	 * To rebuild the options, `enableGrouping` prop value also needs to be true.
	 * @returns {array}
	 */
	buildGroupedOptions = () => {
		const { options, optionValue, groupOptionValue, enableGrouping } =
			this.props;
		const groupedKeys = {};
		const groupedOptions = reduce(
			options,
			(_groupedOptions, option) => {
				if (enableGrouping && isString(groupOptionValue)) {
					const { [groupOptionValue]: groupValue } = option;

					if (!isNumber(groupedKeys[groupValue])) {
						groupedKeys[groupValue] = _groupedOptions.length;

						// option is put into its group bucket based on the `groupOptionValue` prop value
						_groupedOptions[groupedKeys[groupValue]] = [
							{
								...option,
								[optionValue]: `group_${groupValue}`,
								isGroup: true,
								className: 'dropdown-group-item',
								items: [],
							},
						];
					}

					_groupedOptions[groupedKeys[groupValue]][0].items.push({
						...option,
						className: 'dropdown-item',
					});
				} else {
					_groupedOptions.push({
						...option,
						className: 'dropdown-item',
					});
				}

				return _groupedOptions;
			},
			[]
		);

		return flatten(groupedOptions);
	};

	/**
	 * Rebuild grouped options array.
	 */
	updateState = () => {
		this.setState({
			groupedOptions: this.buildGroupedOptions(this.props),
		});
	};

	/**
	 * Render option item template.
	 * @param {object}
	 * @returns {ReactComponent|string}
	 */
	itemTemplate = params => {
		const { groupItemTemplate, itemTemplate, optionLabel } = this.props;

		if (params.isGroup) {
			return groupItemTemplate
				? groupItemTemplate(params)
				: params[optionLabel];
		} else {
			return itemTemplate ? itemTemplate(params) : params[optionLabel];
		}
	};

	focusHandler = e => {
		const { onFocus, dataqa } = this.props;

		Analytics.trackSegmentEvent({
			trackingData: {
				field_type: 'dropdown',
				field_name_dataqa: `ui-grouped-dropdown-${dataqa}`,
			},
			eventType: SegmentEventTypes.UserInputStarted,
		});

		if (isFunction(onFocus)) {
			onFocus(e);
		}
	};

	blurHandler = e => {
		const { onBlur, dataqa } = this.props;

		Analytics.trackSegmentInputEvent({
			trackingData: {
				field_type: 'dropdown',
				field_name_dataqa: `ui-grouped-dropdown-${dataqa}`,
			},
			eventType: SegmentEventTypes.UserInputCompleted,
			fieldValue: e.target?.value,
		});

		if (isFunction(onBlur)) {
			onBlur(e);
		}
	};

	render() {
		const {
			className,
			optionLabel,
			optionValue,
			isInputInvalid,
			dataqa,
			groupOptionLabel,
			groupOptionValue,
			groupItemTemplate,
			enableGrouping,
			showServicesOnly,
			rateOptionValue,
			...config
		} = this.props;
		const dropdownClassName = classnames(className, {
			'invalid-component': isInputInvalid,
		});
		const { groupedOptions } = this.state;

		return (
			<div
				className="common-ui-grouped-dropdown common-ui-dropdown"
				data-qa={`ui-grouped-dropdown-${dataqa}`}
			>
				<Dropdown
					{...config}
					dataqa={`ui-grouped-dropdown-${dataqa}`}
					options={groupedOptions}
					optionLabel={optionLabel}
					optionValue={optionValue}
					dataKey={optionValue}
					optionGroupChildren="items"
					optionGroupLabel={groupOptionLabel}
					itemTemplate={this.itemTemplate}
					optionGroupTemplate={groupItemTemplate}
					className={dropdownClassName}
					onFocus={this.focusHandler}
					onBlur={this.blurHandler}
				/>
			</div>
		);
	}
}

WrappedGroupedDropdown.defaultProps = {
	enableGrouping: true,
	optionLabel: 'label',
	optionValue: 'value',
	dataqa: 'common',
};

WrappedGroupedDropdown.propTypes = {
	// `true` to group the options
	enableGrouping: PropTypes.bool,

	// Name of the group value field of an option (if enableGrouping is `true`)
	groupOptionValue: PropTypes.string,

	// Name of the group label field of an option
	groupOptionLabel: PropTypes.string,

	// An array of options to be populated in the dropdown
	options: PropTypes.array,

	// Name of the label field of an option
	optionLabel: PropTypes.string,

	// Name of the value field of an option
	optionValue: PropTypes.string,

	// Additional classname(s) assgined to the component
	className: PropTypes.string,

	// The template of an option group item
	groupItemTemplate: PropTypes.elementType,

	// The template of an option item
	itemTemplate: PropTypes.elementType,

	// The 'dataqa' attribute added to the DOM
	dataqa: PropTypes.string,
};

export const WrappedGroupedDropdownWithLabel = LabelWrapper(
	WrappedGroupedDropdown
);
